"use client"

import type React from "react"
import { useEffect, useRef, useState } from "react"
import { Leaf, Users, Clock, Map, Globe } from "lucide-react"
import type { StatsBlock as StatsBlockProps } from "@/payload-types"
import { bgClass } from '@/utilities/bgClass'

type Props = StatsBlockProps

const iconMapping: Record<"Map" | "Leaf" | "Users" | "Clock" | "Globe", React.ComponentType<any>> = {
  Map,
  Leaf,
  Users,
  Clock,
  Globe,
}

const dividerClasses = (index: number) => {
  // Vertical dividers:
  // - On 2-column layout (mobile/tablet): show on odd columns (index 1, 3)
  // - On 4-column layout (desktop): show on all except first (index 1, 2, 3)
  const showVerticalMobile = index % 2 !== 0
  const showVerticalDesktop = index > 0

  // Mobile/tablet vertical dividers (2-column layout)
  const verticalMobile = showVerticalMobile
    ? ' before:content-[""] before:absolute before:-left-10 md:before:-left-14 before:top-1/2 before:-translate-y-1/2 before:h-[66%] before:w-px before:bg-minigreen-3 lg:before:hidden'
    : ""

  // Desktop vertical dividers (4-column layout)
  const verticalDesktop = showVerticalDesktop
    ? ' after:content-[""] after:absolute after:-left-14 after:top-1/2 after:-translate-y-1/2 after:h-[66%] after:w-px after:bg-minigreen-3 after:hidden lg:after:block'
    : ""

  return `relative${verticalMobile}${verticalDesktop}`
}

function animateCounter(element: HTMLElement, target: number, prefix: string, suffix: string, duration = 2000) {
  const start = 0
  const startTime = performance.now()

  function update(currentTime: number) {
    const elapsed = currentTime - startTime
    const progress = Math.min(elapsed / duration, 1)

    // Easing function (ease-in-out)
    const easeProgress = progress < 0.5 ? 2 * progress * progress : 1 - Math.pow(-2 * progress + 2, 2) / 2

    const current = Math.round(start + (target - start) * easeProgress)
    element.innerHTML = `${prefix}${current.toLocaleString()}${suffix}`

    if (progress < 1) {
      requestAnimationFrame(update)
    }
  }

  requestAnimationFrame(update)
}

export const StatsBlock: React.FC<Props> = ({ title, stats, background }) => {
  const [hasAnimated, setHasAnimated] = useState(false)
  const [animatedCards, setAnimatedCards] = useState<Set<number>>(new Set())
  const statsRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const container = statsRef.current
    if (!container || hasAnimated) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true)

            const children = Array.from(container.children).filter(
              (child) => !child.classList.contains("absolute"),
            ) as HTMLElement[]

            stats?.forEach((stat, index) => {
              const card = children[index]
              if (!card || animatedCards.has(index)) return

              setAnimatedCards((prev) => new Set(prev).add(index))

              setTimeout(() => {
                card.classList.remove("opacity-0", "translate-y-6")
                card.classList.add("opacity-100", "translate-y-0")

                const numberEl = card.querySelector(".number") as HTMLElement | null
                if (numberEl) {
                  const target = stat.value ?? 0
                  const prefix = stat.prefix || ""
                  const suffix = stat.suffix || ""
                  animateCounter(numberEl, target, prefix, suffix)
                }
              }, index * 300)
            })

            observer.disconnect()
          }
        })
      },
      { threshold: 0.3 },
    )

    observer.observe(container)

    return () => observer.disconnect()
  }, [hasAnimated, stats, animatedCards])

  return (
    <section className={`${bgClass(background)} py-10 md:py-12`}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {title ? (
          <h2 className="text-2xl md:text-3xl font-bold text-minigreen-6 text-left mb-6">{title}</h2>
        ) : (
          <h2 className="sr-only">Impact statistics</h2>
        )}

        <div
          ref={statsRef}
          className="relative grid grid-cols-2 lg:grid-cols-4
                     gap-y-12 md:gap-y-14
                     gap-x-14 md:gap-x-20 lg:gap-x-28"
        >
          {/* <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 lg:hidden pointer-events-none z-10">
            <div className=" dark:bg-minigreen-bg-dark p-2 rounded-full">
              <Globe className="w-20 h-20 text-minigreen-6 dark:text-minigreen-1" />
            </div>
          </div> */}

          {stats?.map((stat, index) => {
            const Icon = iconMapping[stat.icon ?? "Clock"]
            return (
              <div
                key={stat.label ?? index}
                className={`text-center py-6 md:py-8 opacity-0 translate-y-6 transition-all duration-500 ${dividerClasses(index)}`}
              >
                {/* lower two icons in grid */}
                <div
                  className={
                    index >= 2
                      ? 'relative before:content-[""] before:absolute before:left-1/2 before:-translate-x-1/2 before:-top-6 md:before:-top-8 before:w-[66%] before:h-px before:bg-minigreen-3 lg:before:hidden pt-10 sm:pt-12 md:pt-14 lg:pt-0'
                      : ""
                  }
                >
                  <Icon className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 mx-auto mb-4 text-minigreen-6 dark:text-minigreen-1" />

                  <h3 className="text-3xl md:text-4xl font-bold text-minigreen-6 dark:text-minigreen-1 mb-2">
                    <span className="number">0</span>
                  </h3>

                  {stat.label ? (
                    <p className="text-neutral-700 dark:text-neutral-200 text-base md:text-lg leading-relaxed">
                      {stat.label}
                    </p>
                  ) : null}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
